/**
 * SphereBrand — marca "Sphere" proposta no pacote de design (decisão do dono:
 * substitui o wordmark Nava no shell, no splash e no login).
 *
 * Símbolo: esfera em wireframe + constelação (reconstrução vetorial do mockup,
 * `assets/sphere/sphere-mark.svg`), desenhado inline para herdar as cores do tema.
 * Wordmark em HTML (Inter 800) sobre o símbolo, como na imagem aprovada.
 *
 * `size` = altura do símbolo em px. `compact` mostra só o símbolo com a inicial
 * (sidebar de 76 px). A constelação gira devagar (CSS, desligada com movimento
 * reduzido / data-motion="off").
 */
import { useId } from 'react';
import { C } from '@/theme/palette';
import { BRANDING } from '@/theme/branding';

export function SphereMark({ size = 92, spin = true }) {
  const gid = `sp-brand-glow-${useId().replace(/:/g, '')}`;
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={spin ? 'sp-brand-mark sp-brand-mark--spin' : 'sp-brand-mark'}
      style={{ overflow: 'visible', display: 'block' }}
    >
      <defs>
        <radialGradient id={gid} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={C.cyber} stopOpacity="0.22" />
          <stop offset="100%" stopColor={C.cyber} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill={`url(#${gid})`} />
      <g fill="none" stroke={C.neutralDim} strokeWidth=".6" opacity=".85">
        <circle cx="50" cy="50" r="38" />
        <ellipse cx="50" cy="50" rx="21" ry="38" />
        <ellipse cx="50" cy="50" rx="38" ry="15" />
      </g>
      <g className="sp-brand-constellation">
        <g fill="none" stroke={C.neutralDim} strokeWidth=".5" opacity=".7">
          <path d="M20 27L66 15 85 52 36 84 13 53 66 15M20 27L79 76 36 84 42 13 85 52 20 27M13 53L79 76 66 15" />
        </g>
        <g fill={C.digital}>
          <circle cx="20" cy="27" r="1.6" />
          <circle cx="66" cy="15" r="1.4" />
          <circle cx="85" cy="52" r="1.4" />
          <circle cx="36" cy="84" r="1.4" />
          <circle cx="79" cy="76" r="1.2" />
          <circle cx="13" cy="53" r="1.2" />
        </g>
      </g>
    </svg>
  );
}

export function SphereBrand({ size = 92, compact = false, spin = true }) {
  const fontSize = compact ? Math.round(size * 0.24) : Math.round(size * 0.22);
  return (
    <span
      role="img"
      aria-label={BRANDING.logoAlt}
      className="sp-brand"
      style={{
        position: 'relative', display: 'inline-grid', placeItems: 'center',
        width: size, height: size, flexShrink: 0,
      }}
    >
      <span style={{ position: 'absolute', inset: 0 }}><SphereMark size={size} spin={spin} /></span>
      <span
        aria-hidden="true"
        style={{
          position: 'relative', zIndex: 1, fontWeight: 800, fontSize,
          letterSpacing: compact ? '-0.5px' : '-1px', color: C.neutral, lineHeight: 1,
          textShadow: `0 0 12px ${C.digital}66`,
        }}
      >
        {compact ? 'S' : 'Sphere'}
      </span>
    </span>
  );
}
